import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  RefreshCw,
  Trash2,
  Eye,
  AlertCircle,
  X as XIcon
} from 'lucide-react';
import { celeryJobService } from '../../services/celeryJobService';
import { showSuccess, showError } from '../../utils/notificationHandler';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';

const CeleryQueueMonitor = () => {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchData();
    const interval = autoRefresh ? setInterval(fetchData, 5000) : null;
    return () => { if (interval) clearInterval(interval); };
  }, [autoRefresh]);

  const fetchData = async () => {
    try {
      // Don't show loading state on refresh (prevents flashing)
      const isInitialLoad = loading && !stats;
      
      const [tasksData, statsData] = await Promise.all([
        celeryJobService.listRecentTasks(50),
        celeryJobService.getQueueStats()
      ]);
      
      setTasks(tasksData);
      setStats(statsData);
      setLastUpdated(new Date());
      
      // Only set loading to false on initial load
      if (isInitialLoad) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching queue data:', error);
      // Only show error on initial load, not on refresh
      if (loading && !stats) {
        showError(error, 'Failed to fetch queue data');
      }
      setLoading(false);
    }
  };

  const handleCancelTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to cancel this task?')) return;
    try {
      await celeryJobService.cancelTask(taskId);
      showSuccess('Task cancelled successfully');
      fetchData();
    } catch (error) {
      showError(error, 'Failed to cancel task');
    }
  };

  const handleViewTask = async (taskId) => {
    try {
      const taskStatus = await celeryJobService.getTaskStatus(taskId);
      setSelectedTask(taskStatus);
    } catch (error) {
      showError(error, 'Failed to fetch task details');
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      SUCCESS: <CheckCircle className="w-5 h-5 text-green-500" />,
      FAILURE: <XCircle className="w-5 h-5 text-red-500" />,
      PENDING: <Clock className="w-5 h-5 text-gray-400" />,
      STARTED: <Loader className="w-5 h-5 text-blue-500 animate-spin" />,
      PROGRESS: <Loader className="w-5 h-5 text-blue-500 animate-spin" />,
      RETRY: <RefreshCw className="w-5 h-5 text-orange-500" />
    };
    return icons[status] || <AlertCircle className="w-5 h-5 text-gray-400" />;
  };

  const getStatusBadge = (status) => {
    const colors = {
      SUCCESS: 'bg-green-100 text-green-800',
      FAILURE: 'bg-red-100 text-red-800',
      PENDING: 'bg-gray-100 text-gray-800',
      STARTED: 'bg-blue-100 text-blue-800',
      PROGRESS: 'bg-blue-100 text-blue-800',
      RETRY: 'bg-orange-100 text-orange-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
    >
      <div className="flex items-center">
        <div className="p-3 rounded-lg bg-gray-50">
          <Icon className={`h-6 w-6 ${color}`} />
        </div>
        <div className="ml-5">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">{title}</p>
          <motion.p
            key={value}
            initial={{ opacity: 0.7, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="text-3xl font-bold text-gray-900 mt-2"
          >
            {loading && !stats ? (
              <div className="animate-pulse h-9 bg-gray-200 rounded w-20"></div>
            ) : (
              value ?? '0'
            )}
          </motion.p>
        </div>
      </div>
    </motion.div>
  );

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto" />
          <p className="mt-2 text-gray-600">Loading queue data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Task Queue"
        description="Monitor and manage background tasks"
        meta={lastUpdated && (
          <span className="text-xs text-gray-500">
            Updated: {lastUpdated.toLocaleTimeString()}
          </span>
        )}
        actions={
          <>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Auto-refresh (5s)</span>
            </label>
            <Button
              variant="secondary"
              onClick={fetchData}
              disabled={loading}
              icon={<RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />}
            >
              Refresh
            </Button>
          </>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard title="Active" value={stats?.active_tasks} icon={Activity} color="text-blue-600" />
        <StatCard title="Queued" value={stats?.queued_tasks} icon={Clock} color="text-orange-600" />
        <StatCard title="Completed Today" value={stats?.completed_today} icon={CheckCircle} color="text-green-600" />
        <StatCard title="Failed Today" value={stats?.failed_today} icon={XCircle} color="text-red-600" />
      </div>

      {/* Task List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Recent Tasks</h3>
          <p className="mt-1 text-sm text-gray-500">Last 50 tasks across all queues</p>
        </div>
        <div className="divide-y divide-gray-200">
          {loading && !stats ? (
            <div className="p-8 text-center">
              <Loader className="w-6 h-6 text-gray-400 animate-spin mx-auto" />
            </div>
          ) : tasks.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-8 text-center text-gray-500"
            >
              <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium">No tasks found</p>
              <p className="text-xs text-gray-400 mt-1">Tasks will appear here as they are created</p>
            </motion.div>
          ) : (
            tasks.map((task, index) => (
              <motion.div
                key={task.task_id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, delay: index * 0.02 }}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <motion.div
                      key={`${task.task_id}-${task.status}`}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      {getStatusIcon(task.status)}
                    </motion.div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">{task.task_name || 'Unknown Task'}</p>
                      <p className="text-sm text-gray-500 truncate">ID: {task.task_id?.substring(0, 8)}...</p>
                      {task.date_created && (
                        <p className="text-xs text-gray-400 mt-1">{formatTimestamp(task.date_created)}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 ml-4">
                    <motion.div
                      key={`${task.task_id}-badge-${task.status}`}
                      initial={{ scale: 0.9, opacity: 0.8 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      {getStatusBadge(task.status)}
                    </motion.div>
                    <button
                      onClick={() => handleViewTask(task.task_id)}
                      className="p-2 hover:bg-gray-100 rounded transition-colors"
                      title="View details"
                    >
                      <Eye className="w-4 h-4 text-gray-600" />
                    </button>
                    {(task.status === 'PENDING' || task.status === 'STARTED' || task.status === 'PROGRESS') && (
                      <button
                        onClick={() => handleCancelTask(task.task_id)}
                        className="p-2 hover:bg-red-100 rounded text-red-600 transition-colors"
                        title="Cancel task"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-bold text-gray-900">Task Details</h3>
              <button
                onClick={() => setSelectedTask(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <span className="font-medium text-gray-700">Task ID:</span>
                <span className="ml-2 text-gray-600 font-mono text-sm">{selectedTask.task_id}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Status:</span>
                <span className="ml-2">{getStatusBadge(selectedTask.state)}</span>
              </div>
              {selectedTask.progress !== undefined && (
                <div>
                  <span className="font-medium text-gray-700">Progress:</span>
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${selectedTask.progress}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{selectedTask.progress}% - {selectedTask.message}</p>
                  </div>
                </div>
              )}
              {selectedTask.result && (
                <div>
                  <span className="font-medium text-gray-700">Result:</span>
                  <pre className="mt-2 p-3 bg-gray-50 rounded text-sm overflow-x-auto border border-gray-200">
                    {JSON.stringify(selectedTask.result, null, 2)}
                  </pre>
                </div>
              )}
              {selectedTask.error && (
                <div>
                  <span className="font-medium text-red-700">Error:</span>
                  <pre className="mt-2 p-3 bg-red-50 rounded text-sm overflow-x-auto border border-red-200 text-red-800">
                    {selectedTask.error}
                  </pre>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CeleryQueueMonitor;
